'use client'
import { motion } from 'framer-motion'
import { useLang } from '../../context/LangContext'
import { t, productPaaS } from '../../lib/translations'
import ProductLanding from '../../components/ProductLanding'
import Icon from '../../components/Icon'
import { s3AssetUrl } from '../../lib/s3'

const featureIcons = ['refresh', 'shield', 'lightning', 'container', 'shield-check', 'globe', 'chart-bar', 'currency-dollar'] as const

const projectTypes = [
  { name: 'React', slug: 'react' },
  { name: 'Vue', slug: 'vue' },
  { name: 'Angular', slug: 'angular' },
  { name: 'JavaScript', slug: 'javascript' },
  { name: 'Laravel', slug: 'laravel' },
  { name: 'NodeJS', slug: 'nodejs' },
  { name: 'ExpressJS', slug: 'expressjs' },
  { name: 'NuxtJS', slug: 'nuxt' },
  { name: 'NextJS', slug: 'nextjs' },
  { name: 'Golang', slug: 'golang' },
  { name: 'Django', slug: 'django' },
  { name: 'FastAPI', slug: 'fastapi' },
  { name: 'FlutterPWA', slug: 'flutter' },
  { name: 'Docker', slug: 'docker' },
  { name: 'Supervisor', slug: 'supervisor' },
  { name: 'LaravelReverb', slug: 'laravel-reverb' },
  { name: 'StaticHTML', slug: 'HTML5' },
  { name: 'Dotnet', slug: 'dotnet' },
]

export default function PaasPage() {
  const lang = useLang()
  const features = productPaaS.features.map((f, i) => ({
    title: t(lang, f.title),
    description: t(lang, f.description),
    icon: <Icon name={featureIcons[i]} className="w-6 h-6" strokeWidth={1.5} />,
  }))
  const benefits = productPaaS.benefits.map((b) => ({ title: t(lang, b.title), desc: t(lang, b.desc) }))

  return (
    <ProductLanding
      id="paas"
      title={t(lang, productPaaS.title)}
      subtitle={t(lang, productPaaS.subtitle)}
      description={t(lang, productPaaS.description)}
      heroBg="bg-gradient-to-b from-[#7c3aed]/5 via-transparent to-transparent"
      heroGlow="bg-[#8b5cf6]/10"
      features={features}
      benefits={benefits}
    >
      <section className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {t(lang, productPaaS.projectTypesHeading)}
            </h2>
            <p className="mt-4 text-lg text-[#94a3b8] max-w-2xl mx-auto">
              {t(lang, productPaaS.projectTypesDesc)}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {projectTypes.map((pt, i) => (
              <motion.div
                key={pt.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-300"
              >
                <div className="h-12 flex items-center justify-center">
                  <img
                    src={s3AssetUrl(pt.slug)}
                    alt={pt.name}
                    className="h-full w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
                <span className="text-sm text-[#94a3b8] group-hover:text-white transition-colors duration-300 text-center leading-tight">
                  {pt.name}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </ProductLanding>
  )
}
